import { Search } from 'lucide-react';

import { useState } from 'react';

import type { ClaimLinkPerson } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { LinkFooter } from './LinkFooter';

/** Twelve or more names get a search box above. */
export const SEARCH_FROM = 12;

export interface WhoAreYouProps {
  title: string;
  payer: string;
  /** Already formatted for the page language. */
  date: string;
  /** The bill's total, formatted with its currency. */
  amount: string;
  items: number;
  pickedCount: number;
  /** The server's head count; the listed names can differ from it. */
  total: number;
  /** Members and placeholders alike; the payer is left off the grid, as they pick in the app. */
  people: ClaimLinkPerson[];
  onPick: (userId: string) => void;
}

const fold = (value: string) =>
  value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase();

type ChoiceState = Exclude<ClaimLinkPerson['state'], 'paid'>;

const PILL: Record<ChoiceState, string> = {
  picked:
    'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
  notYet: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

export function WhoAreYou({
  title,
  payer,
  date,
  amount,
  items,
  pickedCount,
  total,
  people,
  onPick,
}: WhoAreYouProps) {
  const { t } = useTranslation('claimLink');
  const [query, setQuery] = useState('');
  const choices = people.filter(
    (p): p is ClaimLinkPerson & { state: ChoiceState } => p.state !== 'paid'
  );
  const searchable = choices.length >= SEARCH_FROM;
  const needle = fold(query.trim());
  const shown =
    searchable && needle
      ? choices.filter(p => fold(p.name).includes(needle))
      : choices;

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('who.header', { payer, date })}
        </p>
        <p className="break-words text-xl font-semibold text-gray-900 dark:text-gray-50">
          {title}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('who.summary', {
            amount,
            items: t('who.items', { count: items }),
            people: t('who.people', { count: total }),
          })}
        </p>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {t('who.pickedSoFar', { count: pickedCount })}
        </p>
      </header>

      <section aria-labelledby="claim-link-who" className="space-y-3">
        <div>
          <h1
            id="claim-link-who"
            className="text-2xl font-bold text-gray-900 dark:text-gray-50"
          >
            {t('who.title')}
          </h1>
          <p className="mt-1 text-base text-gray-700 dark:text-gray-300">
            {t('who.lead')}
          </p>
        </div>

        {searchable ? (
          <label className="relative block">
            <span className="sr-only">{t('who.search')}</span>
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={t('who.search')}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-base text-gray-900 placeholder:text-gray-500 focus:border-primary-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-400"
            />
          </label>
        ) : null}

        <ul className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2">
          {shown.map(person => {
            const body = (
              <>
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                >
                  {person.name.charAt(0).toLocaleUpperCase()}
                </span>
                <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
                  <span className="break-words text-base font-medium text-gray-900 dark:text-gray-50">
                    {person.name}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${PILL[person.state]}`}
                  >
                    {t(`who.state.${person.state}`)}
                  </span>
                </span>
              </>
            );
            return (
              <li key={person.userId}>
                <button
                  type="button"
                  onClick={() => onPick(person.userId)}
                  data-testid={`claim-link-person-${person.userId}`}
                  className="flex min-h-14 w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left hover:border-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800"
                >
                  {body}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <p>{t('who.notListed', { payer })}</p>
          <p>{t('who.change', { payer })}</p>
        </div>
      </section>

      <LinkFooter payer={payer} />
    </div>
  );
}

export default WhoAreYou;
